import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { message } from 'ant-design-vue'
import { 
  loginAPI, 
  getUserInfoAPI, 
  switchTenantAPI, 
  getAvailableTenantsAPI,
  switchStoreAPI,
  getAvailableStoresAPI,
  switchDepartmentAPI,
  getAvailableDepartmentsAPI,
  refreshTokenAPI
} from '@/services/auth'
import { safeStorage } from '@/utils/storage'

export const useUserStore = defineStore('user', () => {
  // 状态
  const user = ref(null)
  const tenant = ref(null)
  const tokens = ref(null)
  const loading = ref(false)
  const availableTenants = ref([])
  const availableStores = ref([])
  const currentStore = ref(null)
  const availableDepartments = ref([])
  const currentDepartment = ref(null)
  const refreshTimer = ref(null)
  // 全局“只看个人”开关（默认关闭）
  const onlyPersonal = ref(false)

  // 计算属性
  const isAuthenticated = computed(() => {
    return !!(tokens.value && tokens.value.access && user.value)
  })

  const userName = computed(() => {
    return user.value ? `${user.value.first_name}${user.value.last_name}` : ''
  })

  const tenantName = computed(() => {
    return tenant.value ? tenant.value.tenant_name : ''
  })

  const storeName = computed(() => {
    return currentStore.value ? currentStore.value.store_name : ''
  })

  const departmentName = computed(() => {
    return currentDepartment.value ? currentDepartment.value.department_name : ''
  })

  const hasMultipleTenants = computed(() => {
    return availableTenants.value.length > 1
  })

  const hasMultipleStores = computed(() => {
    return availableStores.value.length > 1
  })

  const hasMultipleDepartments = computed(() => {
    return availableDepartments.value.length > 1
  })

  // 设置定时刷新令牌
  const setupTokenRefresh = () => {
    // 清除现有的定时器
    if (refreshTimer.value) {
      clearInterval(refreshTimer.value)
    }
    
    // 每24小时刷新一次令牌
    refreshTimer.value = setInterval(async () => {
      if (tokens.value && tokens.value.refresh) {
        try {
          const response = await loginAPI({
            refresh: tokens.value.refresh
          })
          
          // 更新令牌
          tokens.value = response.tokens
          safeStorage.setItem('tokens', JSON.stringify(response.tokens))
        } catch (error) {
          console.error('自动刷新令牌失败:', error)
        }
      }
    }, 24 * 60 * 60 * 1000) // 24小时
  }

  // 方法
  const login = async (credentials) => {
    loading.value = true
    try {
      const response = await loginAPI(credentials)
      
      // 检查是否需要选择租户
      if (response.need_tenant_selection) {
        availableTenants.value = response.available_tenants || []
        return {
    // 全局“只看个人”
    onlyPersonal,
          needTenantSelection: true,
          availableTenants: response.available_tenants || [],
          message: response.message
        }
      }
      
      // 直接登录成功
      user.value = response.user
      tenant.value = response.tenant
      tokens.value = response.tokens
      availableStores.value = response.available_stores || []
      currentStore.value = response.current_store || null
      availableDepartments.value = response.available_departments || []
      currentDepartment.value = response.current_department || null
      
      // 保存到本地存储
      safeStorage.setItem('user', JSON.stringify(response.user))
      if (response.tenant) {
        safeStorage.setItem('tenant', JSON.stringify(response.tenant))
      }
      safeStorage.setItem('tokens', JSON.stringify(response.tokens))
      if (response.current_store) {
        safeStorage.setItem('currentStore', JSON.stringify(response.current_store))
      }
      if (response.current_department) {
        safeStorage.setItem('currentDepartment', JSON.stringify(response.current_department))
      }
      
      // 设置令牌自动刷新
      setupTokenRefresh()
      
      message.success(response.message || '登录成功')
      return response
    } catch (error) {
      console.error('登录失败:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || '登录失败'
      message.error(errorMessage)
      throw error
    } finally {
      loading.value = false
    }
  }

  const selectTenant = async (tenantId) => {
    loading.value = true
    try {
      const response = await switchTenantAPI(tenantId)
      
      user.value = response.user
      tenant.value = response.tenant
      tokens.value = response.tokens
      availableStores.value = response.available_stores || []
      currentStore.value = response.current_store || null
      availableDepartments.value = response.available_departments || []
      currentDepartment.value = response.current_department || null
      
      // 保存到本地存储
      safeStorage.setItem('user', JSON.stringify(response.user))
      if (response.tenant) {
        safeStorage.setItem('tenant', JSON.stringify(response.tenant))
      }
      safeStorage.setItem('tokens', JSON.stringify(response.tokens))
      if (response.current_store) {
        safeStorage.setItem('currentStore', JSON.stringify(response.current_store))
      } else {
        safeStorage.removeItem('currentStore')
      }
      if (response.current_department) {
        safeStorage.setItem('currentDepartment', JSON.stringify(response.current_department))
      } else {
        safeStorage.removeItem('currentDepartment')
      }
      
      // 设置令牌自动刷新
      setupTokenRefresh()
      
      message.success(response.message || '切换租户成功')
      return response
    } catch (error) {
      console.error('切换租户失败:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || '切换租户失败'
      message.error(errorMessage)
      throw error
    } finally {
      loading.value = false
    }
  }

  const switchTenant = async (tenantId) => {
    loading.value = true
    try {
      const response = await switchTenantAPI(tenantId)
      
      user.value = response.user
      tenant.value = response.tenant
      tokens.value = response.tokens
      availableStores.value = response.available_stores || []
      currentStore.value = null // 清除当前门店选择
      availableDepartments.value = response.available_departments || []
      currentDepartment.value = null // 清除当前部门选择
      
      // 更新本地存储
      safeStorage.setItem('user', JSON.stringify(response.user))
      if (response.tenant) {
        safeStorage.setItem('tenant', JSON.stringify(response.tenant))
      }
      safeStorage.setItem('tokens', JSON.stringify(response.tokens))
      safeStorage.removeItem('currentStore')
      safeStorage.removeItem('currentDepartment')
      
      // 设置令牌自动刷新
      setupTokenRefresh()
      
      message.success(response.message || '切换租户成功')
      return response
    } catch (error) {
      console.error('切换租户失败:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || '切换租户失败'
      message.error(errorMessage)
      throw error
    } finally {
      loading.value = false
    }
  }

  const switchStore = async (storeId) => {
    loading.value = true
    try {
      const response = await switchStoreAPI(storeId)
      currentStore.value = response.current_store
      
      // 保存当前门店选择
      if (response.current_store) {
        safeStorage.setItem('currentStore', JSON.stringify(response.current_store))
      } else {
        safeStorage.removeItem('currentStore')
      }
      
      message.success(response.message || '切换门店成功')
      return response
    } catch (error) {
      console.error('切换门店失败:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || '切换门店失败'
      message.error(errorMessage)
      throw error
    } finally {
      loading.value = false
    }
  }

  const loadAvailableTenants = async () => {
    try {
      const response = await getAvailableTenantsAPI()
      availableTenants.value = response.available_tenants || []
      return response
    } catch (error) {
      console.error('获取租户列表失败:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || '获取租户列表失败'
      message.error(errorMessage)
    }
  }

  const loadAvailableStores = async () => {
    try {
      const response = await getAvailableStoresAPI()
      availableStores.value = response.available_stores || []
      return response
    } catch (error) {
      console.error('获取门店列表失败:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || '获取门店列表失败'
      message.error(errorMessage)
    }
  }

  const switchDepartment = async (departmentId) => {
    loading.value = true
    try {
      const response = await switchDepartmentAPI(departmentId)
      currentDepartment.value = response.current_department
      
      // 保存当前部门选择
      if (response.current_department) {
        safeStorage.setItem('currentDepartment', JSON.stringify(response.current_department))
      } else {
        safeStorage.removeItem('currentDepartment')
      }
      
      message.success(response.message || '切换部门成功')
      return response
    } catch (error) {
      console.error('切换部门失败:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || '切换部门失败'
      message.error(errorMessage)
      throw error
    } finally {
      loading.value = false
    }
  }

  const loadAvailableDepartments = async () => {
    try {
      const response = await getAvailableDepartmentsAPI()
      availableDepartments.value = response.available_departments || []
      return response
    } catch (error) {
      console.error('获取部门列表失败:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || '获取部门列表失败'
      message.error(errorMessage)
    }
  }

  const logout = () => {
    user.value = null
    tenant.value = null
    tokens.value = null
    availableTenants.value = []
    availableStores.value = []
    currentStore.value = null
    availableDepartments.value = []
    currentDepartment.value = null
    
    // 清除定时器
    if (refreshTimer.value) {
      clearInterval(refreshTimer.value)
      refreshTimer.value = null
    }
    
    // 清除本地存储
    safeStorage.removeItem('user')
    safeStorage.removeItem('tenant')
    safeStorage.removeItem('tokens')
    safeStorage.removeItem('currentStore')
    safeStorage.removeItem('currentDepartment')
    
    message.success('已退出登录')
  }

  const initializeAuth = () => {
    // 从本地存储恢复状态
    try {
      const storedUser = safeStorage.getItem('user')
      const storedTenant = safeStorage.getItem('tenant')
      const storedTokens = safeStorage.getItem('tokens')
      const storedStore = safeStorage.getItem('currentStore')
      const storedDepartment = safeStorage.getItem('currentDepartment')
      
      console.log('初始化认证状态:', {
        hasUser: !!storedUser,
        hasTenant: !!storedTenant,
        hasTokens: !!storedTokens,
        storageType: safeStorage.getStorageType()
      })
      
      if (storedUser && storedTokens) {
        user.value = JSON.parse(storedUser)
        if (storedTenant) {
          tenant.value = JSON.parse(storedTenant)
        }
        tokens.value = JSON.parse(storedTokens)
        
        if (storedStore) {
          currentStore.value = JSON.parse(storedStore)
        }
        
        if (storedDepartment) {
          currentDepartment.value = JSON.parse(storedDepartment)
        }
        
        // 加载可用租户和门店列表
        loadAvailableTenants()
        loadAvailableStores().then(() => {
          // 如果没有当前门店但有可用门店，自动选择第一个
          if (!currentStore.value && availableStores.value.length > 0) {
            currentStore.value = availableStores.value[0]
            safeStorage.setItem('currentStore', JSON.stringify(availableStores.value[0]))
          }
        })
        loadAvailableDepartments().then(() => {
          // 如果没有当前部门但有可用部门，自动选择第一个
          if (!currentDepartment.value && availableDepartments.value.length > 0) {
            currentDepartment.value = availableDepartments.value[0]
            safeStorage.setItem('currentDepartment', JSON.stringify(availableDepartments.value[0]))
          }
        })
        
        // 设置令牌自动刷新
        setupTokenRefresh()
      } else {
        console.log('没有找到有效的认证信息，用户需要重新登录')
      }
    } catch (error) {
      console.error('认证初始化失败:', error)
      // 不要立即logout，给用户机会重新登录
      console.log('认证初始化出错，但不清除状态，用户可以重新登录')
    }
  }

  const updateUserInfo = async () => {
    if (!isAuthenticated.value) return
    
    try {
      const userInfo = await getUserInfoAPI()
      user.value = userInfo
      safeStorage.setItem('user', JSON.stringify(userInfo))
    } catch (error) {
      console.error('Failed to update user info:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || '更新用户信息失败'
      message.error(errorMessage)
    }
  }

  return {
    // 状态
    user,
    tenant,
    tokens,
    loading,
    availableTenants,
    availableStores,
    currentStore,
    availableDepartments,
    currentDepartment,
    refreshTimer,
    
    // 计算属性
    isAuthenticated,
    userName,
    tenantName,
    storeName,
    departmentName,
    hasMultipleTenants,
    hasMultipleStores,
    hasMultipleDepartments,
    
    // 方法
    login,
    selectTenant,
    switchTenant,
    switchStore,
    switchDepartment,
    loadAvailableTenants,
    loadAvailableStores,
    loadAvailableDepartments,
    logout,
    initializeAuth,
    updateUserInfo
  }
})